import { CheckCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "urql";
import {
  AddGoogleAccountMutation,
  CompleteMainGoogleAuthMutation,
} from "../../../graphql/googleAccount.graphql";
import { clearOAuthState, validateOAuthState } from "../../../lib/oauth";

export function DashboardGoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, executeAddGoogleAccount] = useMutation(AddGoogleAccountMutation);
  const [, executeCompleteMainGoogleAuth] = useMutation(
    CompleteMainGoogleAuthMutation,
  );
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing",
  );
  const [error, setError] = useState<string | null>(null);
  // validateOAuthState は sessionStorage を消費する副作用を持つので、
  // React StrictMode / 依存配列の再評価による二重実行を必ず防ぐ
  const hasProcessedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: hasProcessedRef で1回のみ実行を保証
  useEffect(() => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const errorParam = searchParams.get("error");

      if (errorParam) {
        setStatus("error");
        setError("認証がキャンセルされました");
        setTimeout(() => {
          navigate("/employee_interview/google-connect");
        }, 2000);
        return;
      }

      if (!code) {
        setStatus("error");
        setError("認証コードがありません");
        setTimeout(() => {
          navigate("/employee_interview/google-connect");
        }, 2000);
        return;
      }

      if (!state) {
        setStatus("error");
        setError("認証状態が不正です");
        setTimeout(() => {
          navigate("/employee_interview/google-connect");
        }, 2000);
        return;
      }

      try {
        const { purpose } = validateOAuthState(state);
        const redirectUri = `${window.location.origin}/employee_interview/dashboard/google-callback`;

        if (purpose === "additional_account") {
          const result = await executeAddGoogleAccount({
            input: { code, redirectUri },
          });
          if (result.error) {
            throw new Error(
              result.error.message || "アカウントの追加に失敗しました",
            );
          }
        } else {
          const result = await executeCompleteMainGoogleAuth({
            input: { code, redirectUri },
          });
          if (result.error) {
            throw new Error(
              result.error.message || "メインアカウントの連携に失敗しました",
            );
          }
        }

        setStatus("success");
        setTimeout(() => {
          navigate("/employee_interview/google-connect");
        }, 2000);
      } catch (err) {
        clearOAuthState();
        setStatus("error");
        setError(err instanceof Error ? err.message : "エラーが発生しました");
        setTimeout(() => {
          navigate("/employee_interview/google-connect");
        }, 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-subtle-gray)]">
      <div className="flex flex-col items-center gap-4">
        {status === "processing" && (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-primary-500)] border-t-transparent" />
            <p className="text-[var(--color-text-secondary)]">処理中...</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-green-100)]">
              <CheckCircle className="h-6 w-6 text-[var(--color-green-500)]" />
            </div>
            <p className="text-[var(--color-green-600)]">
              アカウントの連携が完了しました
            </p>
            <p className="text-[var(--color-text-tertiary)] text-sm">
              リダイレクト中...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-red-100)]">
              <svg
                className="h-6 w-6 text-[var(--color-red-500)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="text-[var(--color-red-500)]">{error}</p>
            <p className="text-[var(--color-text-tertiary)] text-sm">
              リダイレクト中...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
